import {
  isEntityAttachTrait,
  isEntityDetachTrait,
  isEntityRestore,
  isEntitySoftDelete,
} from './entity-ops.js';
import { type EntityCreatePayload } from './entity.js';
import {
  DEFAULT_FACT_OWNER,
  DEFAULT_FACT_STATUS,
  type FactOwner,
  type FactPayload,
  type FactReviewState,
  type FactStatus,
  type FactValidity,
} from './fact-ops.js';
import { type EntityRoot } from './pack.js';
import { type RevealPayload } from './reveal-ops.js';
import { viewerKey, type ViewerId } from './viewer.js';

/**
 * Predicate slot reserved for entity meta-ops (`entity.create` and, later,
 * `entity.attach_trait` / `entity.archive`). The substrate chains ops by
 * `(subject, predicate)`, so entity-shape ops need their own predicate slot
 * to keep them out of any fact chain. Underscore-prefixed names are
 * reserved by the engine — packs cannot declare them (enforced when the
 * predicate registry validates pack contributions in Phase 1).
 */
export const ENTITY_META_PREDICATE = '__entity';

/**
 * Separator between a repeatable predicate and its instance id in the
 * `EntityState.facts` map key. A singular fact is stored under the bare
 * predicate (`facts['name']`); a repeatable-predicate instance is stored
 * under `facts['government#op_abc123']`. Keeping both in the one map means
 * every existing `facts[predicate]` reader keeps working unchanged, while
 * `factInstancesOf(entity, predicate)` enumerates the grouped instances.
 */
export const FACT_INSTANCE_SEP = '#';

/**
 * Compose the `EntityState.facts` key for a fact. Empty / absent instance
 * → the bare predicate (the singular slot). Non-empty instance →
 * `predicate#instance`.
 */
export function factInstanceKey(predicate: string, instance?: string): string {
  return instance && instance.length > 0 ? `${predicate}${FACT_INSTANCE_SEP}${instance}` : predicate;
}

/** Split a `facts` map key back into `{ predicate, instance }`. */
export function splitFactKey(key: string): { predicate: string; instance: string | null } {
  const idx = key.indexOf(FACT_INSTANCE_SEP);
  if (idx === -1) return { predicate: key, instance: null };
  return { predicate: key.slice(0, idx), instance: key.slice(idx + 1) };
}

/**
 * A single applied version of a fact, derived from the op chain. The
 * version index is the position in the chain, oldest-first.
 */
export interface FactVersion {
  value: unknown;
  written_at_real: string;
  author: string;
}

/**
 * The rolled-up shape of one fact chain: every version that has ever been
 * written (so the UI can render history + rollback), plus the
 * soft-delete flag and current status/owner/validity at the chain tip.
 *
 * `status`, `owner`, and `validity` back-fill from the payload's optional
 * fields with `DEFAULT_FACT_STATUS` / `DEFAULT_FACT_OWNER` / `null` — see
 * `foldFactChainFull` for the full fold semantics. The op log is never
 * rewritten; defaults are applied at projection-rebuild time.
 */
export interface FactState {
  versions: FactVersion[];
  soft_deleted: boolean;
  status: FactStatus;
  owner: FactOwner;
  validity: FactValidity | null;
  /**
   * Viewers this fact has been revealed to (per-attribute reveal, Phase 2 /
   * `30-design-system.md §7.7`). Populated by folding `reveal` ops onto the
   * targeted `(subject, predicate)` fact, deduped by `viewerKey`. The GM is
   * omniscient and never appears here; an empty list means GM-only. This is
   * the GM-side view of effective visibility — the authoritative player
   * projection still flows through `packages/projection`. Reveals are
   * write-once (no removal); order is op-log (first-revealed first).
   */
  reveals: ViewerId[];
  /**
   * Belief provenance (`03 §9` / B3). For a CLONED belief, the source belief's
   * predicate key — the previous link in the "who told whom" chain. Absent on
   * canon and on independently-authored beliefs. GM-side display only.
   */
  source_belief?: string;
  /**
   * Suggestion-set bundle back-pointer (#251). The `bun_…` id of the bundle a
   * member draft was generated in, lifted to the chain tip so the GM-side
   * bundle-review surface can read it without re-folding the op log. Write-once
   * provenance: set on the LLM `fact.create` and preserved across the GM accept
   * edit; absent on hand-authored facts.
   */
  bundle_id?: string;
  /**
   * LLM self-rated confidence 0–1 (#251 §14.6). GM-only — drives the bundle-
   * review sort/threshold; never reaches projection. Write-once provenance
   * (preserved across the accept edit). Absent on hand-authored facts.
   */
  confidence?: number;
  /**
   * Bulk-review marker (#251 §14.2). GM-only provenance, ORTHOGONAL to
   * `status` (`unreviewed_bulk` rides alongside `canon`). Unlike `bundle_id` /
   * `confidence` this is NOT write-once: a `fact.create`/`fact.edit` carrying
   * it sets it; a later `fact.edit` that omits it CLEARS it (the one-click
   * review-pass drops the field). Never consulted by projection.
   */
  review_state?: FactReviewState;
}

/**
 * The client-side projection of one entity, computed by folding the op
 * log. Authority lives in the op log; this shape is rebuildable. Not to
 * be confused with `Entity` from `./entity.ts` — that's the minimal
 * server-side rollup (id/root/traits only).
 */
export interface EntityState {
  id: string;
  root: EntityRoot;
  traits: string[];
  facts: Record<string, FactState>;
  /**
   * True when an `entity.soft_delete` op has been applied without a
   * subsequent `entity.restore` (IV.E.2 / #88). Soft-deleted entities
   * cascade to their facts in the projection — `currentFactValue`
   * still returns the raw value, but consumer surfaces should hide the
   * entity (and consequently its facts) when this is true. The op log
   * retains the entity for the 30-day grace window declared in
   * `entity-ops.ts`.
   */
  soft_deleted: boolean;
}

function authorLabel(author: unknown): string {
  if (author && typeof author === 'object') {
    const kind = (author as { kind?: unknown }).kind;
    if (kind === 'gm') return 'GM';
    if (typeof kind === 'string') return kind;
  }
  return 'GM';
}

interface OpLike {
  subject: string;
  predicate: string;
  /**
   * Optional fact-instance discriminator (mirrors `OpRecord.instance`).
   * Absent / empty → the singular fact slot (every legacy op). Non-empty →
   * one occurrence of a `repeatable` predicate, stored under
   * `predicate#instance` so each occurrence folds into its own chain with
   * its own validity / status / owner / reveal.
   */
  instance?: string;
  payload: unknown;
  created_at_real: string;
  author: unknown;
}

function isEntityCreate(payload: unknown): payload is EntityCreatePayload {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as { kind?: unknown }).kind === 'entity.create'
  );
}

function isRevealPayload(payload: unknown): payload is RevealPayload {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as { kind?: unknown }).kind === 'reveal'
  );
}

function isFactPayload(payload: unknown): payload is FactPayload {
  if (!payload || typeof payload !== 'object') return false;
  const kind = (payload as { kind?: unknown }).kind;
  return (
    kind === 'fact.create' ||
    kind === 'fact.edit' ||
    kind === 'fact.soft_delete' ||
    kind === 'fact.restore' ||
    kind === 'fact.set_status'
  );
}

/**
 * Fold an HLC-ordered stream of ops into a `Record<entityId, EntityState>`
 * with per-fact version history. The op log is the source of truth; this
 * fold is the projection the client view binds against.
 *
 * The caller is responsible for delivering ops in HLC order; the fold
 * itself is order-sensitive and does not re-sort.
 *
 * Identity note (ADR-0004 §1 / #175): predicate ids are opaque and
 * immutable, so a fact folds under exactly its own id — there is no
 * read-time predicate-alias canonicalisation here (the old
 * `previous_names`-as-identity / `predicateAliases` option is removed).
 * Deliberate cross-vocabulary unification is an explicit `pack.alias_vocab`
 * meta-op resolved in `packages/projection` (#178), not a GM-side fold
 * concern.
 *
 * Unknown payload kinds are skipped (forward-compatible: future op kinds
 * the client doesn't recognise yet flow through without breaking the fold).
 */
export function foldOpsIntoEntities(ops: readonly OpLike[]): Record<string, EntityState> {
  const entities: Record<string, EntityState> = {};

  for (const op of ops) {
    if (op.predicate === ENTITY_META_PREDICATE) {
      if (isEntityCreate(op.payload)) {
        // Create is idempotent — replaying a stream that already contains
        // the entity must not clobber accumulated facts.
        if (!entities[op.subject]) {
          entities[op.subject] = {
            id: op.subject,
            root: op.payload.root,
            traits: [...op.payload.traits],
            facts: {},
            soft_deleted: false,
          };
        }
      } else if (isEntitySoftDelete(op.payload)) {
        // Soft-delete is an op; we never rewrite history. A soft-delete
        // op that arrives before the entity.create (out-of-order replay)
        // is a no-op — the create, when it eventually folds, will land
        // with `soft_deleted: false` and the next soft-delete op (if
        // any) will set the flag.
        const existing = entities[op.subject];
        if (existing) existing.soft_deleted = true;
      } else if (isEntityRestore(op.payload)) {
        const existing = entities[op.subject];
        if (existing) existing.soft_deleted = false;
      } else if (isEntityAttachTrait(op.payload)) {
        // Idempotent attach — replaying for a trait already present is a
        // no-op. An attach for a not-yet-created entity is also a no-op
        // (the substrate guarantees create-before-attach for well-behaved
        // streams; out-of-order replay drops the op rather than minting
        // an entity without its root).
        const existing = entities[op.subject];
        if (existing && !existing.traits.includes(op.payload.trait_id)) {
          existing.traits.push(op.payload.trait_id);
        }
      } else if (isEntityDetachTrait(op.payload)) {
        // Idempotent detach — removes the trait if present; no-op
        // otherwise. Facts on the trait's predicates are NEVER mutated
        // here; they stay in the op log and the fold continues to roll
        // them up. The editor's predicate-list derivation walks
        // `entity.traits[]`, so detach hides the rows without
        // destroying the data — re-attaching restores them.
        const existing = entities[op.subject];
        if (existing) {
          const idx = existing.traits.indexOf(op.payload.trait_id);
          if (idx >= 0) existing.traits.splice(idx, 1);
        }
      }
      continue;
    }

    // Reveal ops (per-attribute reveal, Phase 2). The op's envelope
    // predicate is a synthetic `reveal:…` slot; the TARGET fact is named by
    // `payload.predicate`. We fold the viewer onto that fact's `reveals`
    // list so GM surfaces (the reveal chip / Viewer-Matrix popover) can show
    // effective visibility without re-deriving it. The authoritative player
    // projection still runs through `packages/projection` over the same ops.
    if (isRevealPayload(op.payload)) {
      const entity = entities[op.subject];
      if (!entity) continue;
      const target = op.payload.predicate;
      // Reveal payloads carry no `instance`, so a reveal targets the
      // singular fact slot. (Per-instance reveal of a repeatable predicate
      // is a substrate extension beyond this increment.)
      const fact = entity.facts[target];
      // A reveal pointing at a fact that doesn't exist (yet) is dropped — we
      // never mint a phantom fact slot from a reveal alone.
      if (!fact) continue;
      const vk = viewerKey(op.payload.viewer);
      if (!fact.reveals.some((v) => viewerKey(v) === vk)) {
        fact.reveals.push(op.payload.viewer);
      }
      continue;
    }

    if (!isFactPayload(op.payload)) continue;
    const entity = entities[op.subject];
    // A fact op for a not-yet-created entity means the stream is malformed
    // or the entity.create hasn't arrived yet. At WS scope we drop —
    // server arbitration guarantees create-before-fact in well-behaved
    // flows.
    if (!entity) continue;

    // Opaque ids are immutable (ADR-0004 §1), so a fact folds under exactly
    // its own predicate id — no read-time canonicalisation. Deliberate
    // cross-vocabulary unification is `pack.alias_vocab`, resolved in
    // `packages/projection` (#178), not here.
    const predicate = op.predicate;
    // A repeatable predicate's occurrences each carry an `instance` id and
    // fold into their own chain under `predicate#instance`; a singular fact
    // (no instance) keeps the bare predicate key, so every existing reader
    // (`entity.facts[predicate]`) and op is unaffected.
    const factKey = factInstanceKey(predicate, op.instance);

    const fact: FactState = entity.facts[factKey] ?? {
      versions: [],
      soft_deleted: false,
      status: DEFAULT_FACT_STATUS,
      owner: DEFAULT_FACT_OWNER,
      validity: null,
      reveals: [],
    };
    switch (op.payload.kind) {
      case 'fact.create':
        fact.versions.push({
          value: op.payload.value,
          written_at_real: op.created_at_real,
          author: authorLabel(op.author),
        });
        fact.soft_deleted = false;
        fact.status = op.payload.status ?? DEFAULT_FACT_STATUS;
        fact.owner = op.payload.owner ?? DEFAULT_FACT_OWNER;
        fact.validity = op.payload.validity ?? null;
        if (op.payload.source_belief !== undefined) fact.source_belief = op.payload.source_belief;
        // #251 — bundle provenance (write-once on the LLM member create) + the
        // review marker (last-write; create sets it, an edit dropping it clears).
        if (op.payload.bundle_id !== undefined) fact.bundle_id = op.payload.bundle_id;
        if (op.payload.confidence !== undefined) fact.confidence = op.payload.confidence;
        if (op.payload.review_state !== undefined) fact.review_state = op.payload.review_state;
        else delete fact.review_state;
        break;
      case 'fact.edit':
        fact.versions.push({
          value: op.payload.value,
          written_at_real: op.created_at_real,
          author: authorLabel(op.author),
        });
        fact.soft_deleted = false;
        if (op.payload.status !== undefined) fact.status = op.payload.status;
        if (op.payload.owner !== undefined) fact.owner = op.payload.owner;
        if (op.payload.validity !== undefined) fact.validity = op.payload.validity;
        if (op.payload.source_belief !== undefined) fact.source_belief = op.payload.source_belief;
        // #251 — bundle_id / confidence are write-once provenance (an accept
        // edit preserves them); review_state follows last-write so a review-pass
        // edit that omits the field clears the marker.
        if (op.payload.bundle_id !== undefined) fact.bundle_id = op.payload.bundle_id;
        if (op.payload.confidence !== undefined) fact.confidence = op.payload.confidence;
        if (op.payload.review_state !== undefined) fact.review_state = op.payload.review_state;
        else delete fact.review_state;
        break;
      case 'fact.soft_delete':
        fact.soft_deleted = true;
        break;
      case 'fact.restore':
        fact.soft_deleted = false;
        break;
      case 'fact.set_status':
        fact.status = op.payload.status;
        break;
    }
    entity.facts[factKey] = fact;
  }

  return entities;
}

/**
 * Enumerate the instances of a (possibly repeatable) predicate on an
 * entity, in instance-id order for stable rendering. Returns the singular
 * fact (instance `null`) when present, plus every `predicate#instance`
 * occurrence. Soft-deleted facts are included — callers filter via
 * `currentFactValue` / `fact.soft_deleted` so a tombstone still renders in
 * history. A predicate with no facts yields `[]`.
 */
export interface FactInstance {
  /** null for the singular slot; the instance id for a repeatable occurrence. */
  instance: string | null;
  fact: FactState;
}

export function factInstancesOf(entity: EntityState, predicate: string): FactInstance[] {
  const out: FactInstance[] = [];
  for (const [key, fact] of Object.entries(entity.facts)) {
    const split = splitFactKey(key);
    if (split.predicate !== predicate) continue;
    out.push({ instance: split.instance, fact });
  }
  out.sort((a, b) => {
    // Singular slot first, then instances by id.
    if (a.instance === null) return b.instance === null ? 0 : -1;
    if (b.instance === null) return 1;
    return a.instance.localeCompare(b.instance);
  });
  return out;
}

/**
 * Current value of a fact (latest version, respecting soft-delete). Mirrors
 * `foldFactChain` but operates on the rolled-up `FactState` produced by
 * `foldOpsIntoEntities`.
 */
export function currentFactValue(fact: FactState | undefined): unknown {
  if (!fact || fact.soft_deleted) return undefined;
  return fact.versions[fact.versions.length - 1]?.value;
}
